const ROOT = "/api/site/v1/speakers";
const slugPattern = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
export const contentOrder =
  (field = "sortOrder") =>
  (a, b) => {
    const order = (value) =>
      Number.isSafeInteger(value) && value >= 0
        ? value
        : Number.MAX_SAFE_INTEGER;
    return order(a[field]) - order(b[field]) || a.id.localeCompare(b.id);
  };
async function request(path, signal) {
  const controller = new AbortController();
  const cancel = () => controller.abort();
  signal?.addEventListener("abort", cancel, { once: true });
  if (signal?.aborted) cancel();
  const timer = setTimeout(cancel, 20000);
  try {
    const response = await fetch(ROOT + path, {
      credentials: "omit",
      cache: "no-store",
      redirect: "error",
      referrerPolicy: "no-referrer",
      signal: controller.signal,
    });
    const result = await response.json().catch(() => null);
    if (response.status === 404 && result?.error?.code === "not_found")
      return null;
    if (!response.ok || !result || !Object.hasOwn(result, "data"))
      throw Error("unavailable");
    return result.data;
  } catch (error) {
    if (signal?.aborted) throw error;
    throw new Error(
      "Speaker profiles could not connect to the website. Please try again later.",
    );
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener("abort", cancel);
  }
}
function profile(record) {
  return record?.status === "published" &&
    /^[A-Za-z0-9_-]{1,128}$/.test(record.id || "") &&
    slugPattern.test(record.slug || "") &&
    record.slug.length <= 150 &&
    record.name?.trim()
    ? record
    : null;
}
export async function listPublicSpeakers(signal) {
  const records = new Map(),
    cursors = new Set();
  let after = null;
  do {
    const page = await request(
      `?limit=100${after ? `&after=${after}` : ""}`,
      signal,
    );
    if (
      !page ||
      !Array.isArray(page.items) ||
      (page.next !== null && !/^[A-Za-z0-9_-]{1,128}$/.test(page.next || ""))
    )
      throw new Error("Invalid speaker catalogue response.");
    page.items.forEach((value) => {
      const record = profile(value);
      if (record) records.set(record.id, record);
    });
    after = page.next;
    if (after && cursors.has(after))
      throw new Error("Speaker catalogue pagination did not finish.");
    if (after) cursors.add(after);
  } while (after);
  return [...records.values()].sort(contentOrder());
}
export async function getPublicSpeakerBySlug(slug, signal) {
  const value = String(slug || "")
    .trim()
    .toLowerCase();
  if (!slugPattern.test(value) || value.length > 150) return null;
  const result = await request(`/by-slug/${value}`, signal);
  if (result === null) return null;
  const record = profile(result);
  if (!record || record.slug !== value)
    throw new Error("Invalid speaker profile response.");
  return record;
}
